import React, { createContext, useContext } from "react";
import { createSubscription } from "../../../../../api/memberships";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../redux/store";
import useUser from "../../../../../hooks/user/useUser";
import { useRouter } from "next/router";
import {
  executePurchase,
  getOwnedListings,
  getUrlOneTime,
} from "../../../../../api/listings";
import { useSnackbarContext } from "../../../../global/snackbar/SnackbarProvider";
import { ErrorSnackbar } from "../../../../global/snackbar";
import { ListingsFromApi } from "../../../../../interfaces/training-plan/ListingsFromApi";
import { ISwapsAPI } from "../../../../../interfaces/training-plan/ISwapsAPI";

type ListingModalPanOptionsContextProps = {
  handleNormalSubscription: (id: number) => Promise<void>;
  handleOneTimePurchase: () => Promise<void>;
  handleGetOwnedListings: () => Promise<ListingsFromApi>;
  handleExecutePurchase: () => Promise<ISwapsAPI>;
};

const ListingModalOptionsContext = createContext<
  ListingModalPanOptionsContextProps | undefined
>(undefined);

export const ListingModalPlanOptionsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const currentListing = useSelector(
    (state: RootState) => state.trainingPlan.listingData,
  );
  const { user } = useUser();
  const router = useRouter();
  const { setSnackbar } = useSnackbarContext();

  const handleNormalSubscription = async (id: number) => {
    try {
      const createSubscriptionRes = await createSubscription(
        id,
        user,
        currentListing,
      );

      if (createSubscriptionRes.url) {
        void router.push(createSubscriptionRes.url);
        return;
      }
    } catch (e) {
      const error = e as Error;
      setSnackbar(<ErrorSnackbar message={error.message} />);
    }
  };

  const handleOneTimePurchase = async () => {
    try {
      const urlOneTimeRes = await getUrlOneTime(user, currentListing);

      if (urlOneTimeRes?.url) {
        void router.push(urlOneTimeRes.url);
        return;
      }
    } catch (e) {
      throw e;
    }
  };

  const handleGetOwnedListings = async () => {
    try {
      const ownedListings = await getOwnedListings(user);

      return ownedListings;
    } catch (e) {
      const error = e as Error;
      setSnackbar(<ErrorSnackbar message={error.message} />);
    }
  };

  const handleExecutePurchase = async () => {
    try {
      const executePurchaseRes = await executePurchase(currentListing, user);

      return executePurchaseRes;
    } catch (e) {
      throw e;
    }
  };

  return (
    <ListingModalOptionsContext.Provider
      value={{
        handleNormalSubscription,
        handleOneTimePurchase,
        handleExecutePurchase,
        handleGetOwnedListings,
      }}
    >
      {children}
    </ListingModalOptionsContext.Provider>
  );
};

export const useListingModalPlanOptionsContext = () => {
  const context = useContext(ListingModalOptionsContext);

  if (!context) {
    throw new Error(
      "useListingModalPlanOptionsProvider must be used within a ListingModalProvider",
    );
  }

  return context;
};
